import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Shield, MapPin, Users, Star, Check,
  Eye, EyeOff, Lock, MessageCircle, Globe,
  Rocket, Award, Zap, Heart,
  ArrowRight, Menu, X, Linkedin, Twitter
} from "lucide-react";

// ==================== MEDIA ASSETS ====================
const MEDIA = {
  heroMap: "https://static.prod-images.emergentagent.com/jobs/456c376e-78f7-4caf-929b-ff0f683646de/images/00591f9ca2d8b30997dfdabd61915293be2d5773470ed74785dead166e25162b.png",
  badges: "https://static.prod-images.emergentagent.com/jobs/456c376e-78f7-4caf-929b-ff0f683646de/images/bf7439b3521d5e2f31d348932bf9aec142b18577154fc4eb2301015e62853f28.png",
  handsSolidarity: "/hands-solidarity.jpg"
};

// ==================== ANIMATION VARIANTS ====================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// ==================== COMPONENTS ====================

// Glassmorphism Card
const GlassCard = ({ children, className = "", glow = false }) => (
  <div className={`
    bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl
    ${glow ? 'shadow-[0_0_60px_-15px_rgba(255,255,255,0.15)]' : ''}
    ${className}
  `}>
    {children}
  </div>
);

// Section wrapper
const Section = ({ children, className = "", id = "" }) => (
  <section id={id} className={`py-24 md:py-32 px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </section>
);

// ==================== HEADER ====================
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/60 backdrop-blur-2xl border-b border-white/5' : ''
    }`}>
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img 
            src="/logo-header.png" 
            alt="RateShare Logo" 
            className="w-10 h-10 object-contain"
          />
          <span className="text-xl font-bold text-white tracking-tight">RATE<span className="text-amber-400">⭐</span>SHARE</span>
        </Link>
        
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#solution" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Solution</a>
          <a href="#how-it-works" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Fonctionnement</a>
          <a href="#security" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Sécurité</a>
        </div>
        
        <div className="hidden md:flex items-center gap-4">
          <Link to="/auth" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium" data-testid="nav-login">
            Connexion
          </Link>
          <Link 
            to="/auth?mode=register" 
            className="px-5 py-2.5 bg-white text-black rounded-full font-semibold text-sm hover:bg-zinc-200 transition-all"
            data-testid="nav-cta"
          >
            Rejoindre la Beta
          </Link>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white p-2"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
      
      {/* Mobile Menu */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-black/95 backdrop-blur-2xl border-t border-white/5 px-6 py-8 space-y-6"
        >
          <a href="#solution" className="block text-zinc-300 hover:text-white text-lg">Solution</a>
          <a href="#how-it-works" className="block text-zinc-300 hover:text-white text-lg">Fonctionnement</a>
          <a href="#security" className="block text-zinc-300 hover:text-white text-lg">Sécurité</a>
          <div className="pt-4 border-t border-white/10 space-y-4">
            <Link to="/auth" className="block text-zinc-400 text-lg">Connexion</Link>
            <Link to="/auth?mode=register" className="block w-full py-3 bg-white text-black rounded-full font-semibold text-center">
              Rejoindre la Beta
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-zinc-950">
      {/* Background Map */}
      <div className="absolute inset-0">
        <img 
          src={MEDIA.heroMap} 
          alt="Dynamic Map" 
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/60 to-zinc-950" />
      </div>
      
      {/* Animated GPS Dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full bg-sky-400"
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            <div className="absolute inset-0 rounded-full bg-sky-400 animate-ping" />
          </motion.div>
        ))}
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-32 pb-20">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400">
              <MapPin size={14} className="text-pink-400" />
              Lyon, France
              <span className="mx-2 w-1 h-1 rounded-full bg-zinc-600" />
              <Rocket size={14} className="text-sky-400" />
              Lancement Q3 2026
            </div>
          </motion.div>
          
          {/* Main Headline */}
          <motion.h1 
            variants={fadeInUp}
            className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-[0.9]"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
          >
            Agis pour tous,<br />
            <span className="bg-gradient-to-r from-pink-400 via-rose-400 to-amber-400 bg-clip-text text-transparent">
              car l'autre c'est toi.
            </span>
          </motion.h1>
          
          {/* Subtitle */}
          <motion.p 
            variants={fadeInUp}
            className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed"
          >
            La sécurité collective vérifiée par croisement GPS réel.
            <br className="hidden md:block" />
            Construisez votre réputation par vos actes, pas par des avis anonymes.
          </motion.p>
          
          {/* CTAs */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/auth?mode=register"
              className="group px-8 py-4 bg-white text-black rounded-full font-bold text-lg hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-button"
            >
              Rejoindre la Beta
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#how-it-works"
              className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-full font-semibold text-lg hover:bg-white/10 transition-all inline-flex items-center justify-center gap-2"
              data-testid="hero-secondary-cta"
            >
              Voir le fonctionnement
            </a>
          </motion.div>
          
          {/* Floating Stats */}
          <motion.div variants={fadeInUp} className="pt-12 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {[
              { value: "2026", label: "Places gratuites" },
              { value: "250m", label: "Rayon de croisement" },
              { value: "100%", label: "Anonyme" }
            ].map((stat, i) => (
              <GlassCard key={i} className="p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm text-zinc-500 mt-1">{stat.label}</div>
              </GlassCard>
            ))}
          </motion.div>
        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 rounded-full bg-white/40" />
        </div>
      </motion.div>
    </section>
  );
};

// ==================== PROBLEM SECTION ====================
const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const stats = [
    { number: "78%", label: "des Français estiment que l'insécurité augmente", icon: Shield },
    { number: "65%", label: "ne font pas confiance aux avis en ligne", icon: Star },
    { number: "90%", label: "des signalements restent sans suite", icon: Eye }
  ];
  
  return (
    <Section className="bg-black" id="problem">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="space-y-16"
      >
        <motion.div variants={fadeInUp} className="max-w-4xl">
          <span className="text-pink-400 text-sm font-semibold uppercase tracking-wider">Le Problème</span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4 tracking-tight leading-tight">
            La confiance numérique est brisée.
          </h2>
          <p className="text-xl text-zinc-400 mt-6 leading-relaxed">
            Dans un monde où n'importe qui peut laisser un avis anonyme, 
            comment distinguer le vrai du faux ?
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {stats.map((stat, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="p-8 rounded-3xl bg-zinc-900/50 border border-white/5 hover:border-white/10 transition-all"
            >
              <stat.icon className="text-pink-400 mb-4" size={32} />
              <div className="text-5xl md:text-6xl font-black text-white mb-3">
                {stat.number}
              </div>
              <p className="text-zinc-400 text-lg">{stat.label}</p>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          variants={fadeInUp}
          className="p-8 rounded-3xl bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20"
        >
          <p className="text-xl md:text-2xl text-white font-medium text-center">
            "La confiance numérique actuelle repose sur des avis anonymes non vérifiés.
            <br />
            <span className="text-pink-400">Il est temps de changer les règles.</span>"
          </p>
        </motion.div>
      </motion.div>
    </Section>
  );
};

// ==================== SOLIDARITY IMAGE SECTION ====================
const SolidaritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <Section className="bg-zinc-950 overflow-hidden py-16 md:py-24" id="solidarity">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="relative"
      >
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div variants={fadeInUp} className="space-y-6 order-2 md:order-1">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Notre Vision</span>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight">
              Agis pour tous,
              <br />
              <span className="text-pink-400">car l'autre c'est toi.</span>
            </h2>
            <p className="text-lg text-zinc-400 leading-relaxed">
              Chaque croisement est une opportunité de reconnaître quelqu'un pour ses actes positifs. 
              Ensemble, construisons un réseau de confiance basé sur des interactions réelles.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              {[
                { icon: Heart, label: "Entraide" },
                { icon: Users, label: "Communauté" },
                { icon: Shield, label: "Confiance" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                  <item.icon size={16} className="text-pink-400" />
                  <span className="text-sm text-white font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Image */}
          <motion.div 
            variants={fadeInUp}
            className="relative order-1 md:order-2"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-pink-500/10">
              <img 
                src={MEDIA.handsSolidarity}
                alt="Mains unies - Solidarité et confiance"
                className="w-full h-auto object-cover aspect-square"
                data-testid="solidarity-image"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-white/90 text-lg font-medium italic">
                  "La confiance se construit main dans la main"
                </p>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-pink-500/20 blur-3xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 rounded-full bg-amber-500/20 blur-3xl" />
          </motion.div>
        </div>
      </motion.div>
    </Section>
  );
};

// ==================== SOLUTION SECTION ====================
const SolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const comparisons = [
    { name: "Google Reviews", checks: [false, false, true, false], color: "text-zinc-500" },
    { name: "LinkedIn", checks: [false, true, false, false], color: "text-zinc-500" },
    { name: "Waze", checks: [true, false, true, false], color: "text-zinc-500" },
    { name: "RateShare", checks: [true, true, true, true], color: "text-white", highlight: true }
  ];
  
  const features = ["Vérification GPS", "Anti-Fake", "Anonymat", "Double opt-in"];
  
  return (
    <Section className="bg-zinc-950" id="solution">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="space-y-16"
      >
        <motion.div variants={fadeInUp} className="text-center max-w-4xl mx-auto">
          <span className="text-sky-400 text-sm font-semibold uppercase tracking-wider">La Solution</span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4 tracking-tight">
            On ne note pas les personnes.
            <br />
            <span className="text-sky-400">On note les actes.</span>
          </h2>
        </motion.div>
        
        {/* Comparison Grid */}
        <motion.div variants={fadeInUp} className="overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Header */}
            <div className="grid grid-cols-5 gap-4 mb-4">
              <div></div>
              {features.map((f, i) => (
                <div key={i} className="text-center text-sm text-zinc-400 font-medium">{f}</div>
              ))}
            </div>
            
            {/* Rows */}
            {comparisons.map((comp, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className={`grid grid-cols-5 gap-4 p-4 rounded-2xl mb-3 ${
                  comp.highlight 
                    ? 'bg-gradient-to-r from-sky-500/10 to-pink-500/10 border border-sky-500/20' 
                    : 'bg-zinc-900/30'
                }`}
              >
                <div className={`font-semibold ${comp.color}`}>{comp.name}</div>
                {comp.checks.map((check, j) => (
                  <div key={j} className="flex justify-center">
                    {check ? (
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check size={14} className="text-green-400" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center">
                        <X size={14} className="text-zinc-600" />
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Features Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: MapPin, title: "Vérification GPS réelle", desc: "Croisement prouvé par géolocalisation", color: "sky" },
            { icon: EyeOff, title: "Signalement anonyme", desc: "Votre position n'est jamais révélée", color: "green" },
            { icon: Award, title: "Badges de fiabilité", desc: "Citoyen → Gardien → Légende", color: "amber" },
            { icon: Lock, title: "Vie privée protégée", desc: "RGPD natif, données en France", color: "pink" }
          ].map((feat, i) => (
            <motion.div key={i} variants={fadeInUp}>
              <GlassCard className="p-6 h-full hover:border-white/20 transition-all group">
                <div className={`w-12 h-12 rounded-2xl bg-${feat.color}-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feat.icon className={`text-${feat.color}-400`} size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
                <p className="text-zinc-400 text-sm">{feat.desc}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </Section>
  );
};

// ==================== HOW IT WORKS ====================
const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const steps = [
    { icon: MapPin, title: "Croisement GPS", desc: "Deux utilisateurs se croisent à moins de 250m au même moment", color: "sky" },
    { icon: Heart, title: "Acte positif", desc: "Likez anonymement les croisements qui vous ont marqué positivement", color: "pink" },
    { icon: Users, title: "Double opt-in", desc: "Les deux personnes doivent s'apprécier pour créer un Crush", color: "green" },
    { icon: Shield, title: "Réputation vérifiée", desc: "Votre score augmente uniquement sur validation mutuelle", color: "amber" },
    { icon: Award, title: "Badges débloqués", desc: "Progressez de Citoyen à Gardien puis Légende", color: "purple" }
  ];
  
  return (
    <Section className="bg-black" id="how-it-works">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="space-y-16"
      >
        <motion.div variants={fadeInUp} className="text-center">
          <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Comment ça marche</span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4 tracking-tight">
            5 étapes simples
          </h2>
        </motion.div>
        
        <div className="relative">
          {/* Connecting Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-500 via-pink-500 to-amber-500 hidden md:block" />
          
          {/* Steps */}
          <div className="space-y-12">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className={`flex items-center gap-8 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={`flex-1 ${i % 2 === 1 ? 'md:text-right' : ''}`}>
                  <GlassCard glow className="p-6 inline-block">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl bg-${step.color}-500/10 flex items-center justify-center flex-shrink-0`}>
                        <step.icon className={`text-${step.color}-400`} size={28} />
                      </div>
                      <div>
                        <div className="text-xs text-zinc-500 font-medium mb-1">ÉTAPE {i + 1}</div>
                        <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        <p className="text-zinc-400 text-sm mt-1">{step.desc}</p>
                      </div>
                    </div>
                  </GlassCard>
                </div>
                
                {/* Center Dot */}
                <div className="hidden md:flex w-4 h-4 rounded-full bg-white border-4 border-zinc-950 z-10" />
                
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </Section>
  );
};

// ==================== GAMIFICATION ====================
const GamificationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const badges = [
    { name: "Citoyen", color: "green", desc: "Niveau de départ. Commencez à croiser et à construire votre réputation.", unlocks: "Accès de base" },
    { name: "Gardien", color: "sky", desc: "10+ Crushes. Vous êtes reconnu pour vos interactions positives.", unlocks: "Mode Safe Check + Alertes" },
    { name: "Légende", color: "amber", desc: "50+ Crushes. Vous êtes un pilier de la communauté.", unlocks: "Badge vérifié + Priorité" }
  ];
  
  return (
    <Section className="bg-zinc-950" id="gamification">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="space-y-16"
      >
        <motion.div variants={fadeInUp} className="text-center">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Gamification</span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4 tracking-tight">
            Devenez une Légende
          </h2>
          <p className="text-xl text-zinc-400 mt-4 max-w-2xl mx-auto">
            Progressez par vos bonnes actions et débloquez des avantages exclusifs.
          </p>
        </motion.div>
        
        {/* Badges Image */}
        <motion.div variants={fadeInUp} className="flex justify-center">
          <img 
            src={MEDIA.badges} 
            alt="Badges Citoyen Gardien Légende" 
            className="max-w-md w-full"
          />
        </motion.div>
        
        {/* Badge Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {badges.map((badge, i) => (
            <motion.div key={i} variants={fadeInUp}>
              <GlassCard className={`p-6 h-full border-${badge.color}-500/20 hover:border-${badge.color}-500/40 transition-all`}>
                <div className={`text-${badge.color}-400 font-bold text-lg mb-2`}>{badge.name}</div>
                <p className="text-zinc-400 text-sm mb-4">{badge.desc}</p>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-${badge.color}-500/10 text-${badge.color}-400 text-xs font-medium`}>
                  <Zap size={12} />
                  {badge.unlocks}
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </Section>
  );
};

// ==================== SECURITY SECTION ====================
const SecuritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const features = [
    { icon: EyeOff, title: "Position jamais visible", desc: "Seule la ville est partagée" },
    { icon: MapPin, title: "Lieux approximatifs", desc: "Pas de coordonnées exactes" },
    { icon: Eye, title: "Mode invisible", desc: "Désactivez les croisements à tout moment" },
    { icon: MessageCircle, title: "Chat sécurisé", desc: "Messages chiffrés de bout en bout" },
    { icon: Shield, title: "Anti-abus", desc: "Système de modération avancé" },
    { icon: Lock, title: "RGPD natif", desc: "Conçu pour la conformité européenne" }
  ];
  
  return (
    <Section className="bg-black" id="security">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="space-y-16"
      >
        <motion.div variants={fadeInUp} className="text-center">
          <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Sécurité & RGPD</span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4 tracking-tight">
            Vie privée <span className="text-green-400">par défaut</span>
          </h2>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, i) => (
            <motion.div key={i} variants={fadeInUp}>
              <div className="p-6 rounded-2xl bg-zinc-900/30 border border-white/5 hover:border-green-500/20 transition-all">
                <feat.icon className="text-green-400 mb-4" size={28} />
                <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
                <p className="text-zinc-400 text-sm">{feat.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div variants={fadeInUp} className="text-center">
          <GlassCard className="inline-flex items-center gap-3 px-6 py-4">
            <Globe className="text-sky-400" size={24} />
            <span className="text-zinc-300">Données hébergées en <span className="text-white font-semibold">France (OVH)</span></span>
          </GlassCard>
        </motion.div>
      </motion.div>
    </Section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer className="bg-black py-24 md:py-32 px-6 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        {/* Massive Brand */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center">
            <span className="text-[12vw] md:text-[10vw] font-black text-white/10 leading-none tracking-tighter">RATE</span>
            <img 
              src="/logo-512.png" 
              alt="RateShare Logo" 
              className="w-[10vw] md:w-[8vw] h-[10vw] md:h-[8vw] object-contain mx-2"
            />
            <span className="text-[12vw] md:text-[10vw] font-black text-white/10 leading-none tracking-tighter">SHARE</span>
          </div>
        </div>
        
        {/* Company Info */}
        <div className="flex flex-col items-center justify-center gap-4 mb-16">
          <div className="text-center">
            <div className="text-zinc-500 text-sm">SAS RATESHARE</div>
            <div className="text-zinc-400 text-sm mt-2">FONDATEUR</div>
            <div className="text-lg font-semibold text-white">Samy Cherif</div>
            <a href="mailto:contact@rateshare.fr" className="text-pink-400 hover:text-pink-300 transition-colors text-sm mt-2 inline-block">
              contact@rateshare.fr
            </a>
          </div>
        </div>
        
        {/* Links */}
        <div className="flex flex-wrap justify-center gap-8 text-sm text-zinc-500">
          <Link to="/terms" className="hover:text-white transition-colors">Conditions d'utilisation</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Politique de confidentialité</Link>
          <Link to="/legal" className="hover:text-white transition-colors">Mentions légales</Link>
        </div>
        
        {/* Social */}
        <div className="flex justify-center gap-4 mt-8">
          <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all">
            <Twitter size={18} />
          </a>
          <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all">
            <Linkedin size={18} />
          </a>
        </div>
        
        {/* Copyright */}
        <div className="text-center text-zinc-600 text-sm mt-12">
          © 2026 SAS RATESHARE. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
};

// ==================== MAIN PAGE ====================
const LandingPageNew = () => {
  return (
    <div className="bg-zinc-950 min-h-screen" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <SolidaritySection />
        <SolutionSection />
        <HowItWorksSection />
        <GamificationSection />
        <SecuritySection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPageNew;
